<script lang="ts">
  // libs
  import * as LocaleString from "@shared/svelte/Components/DateFormatter/LocaleString.svelte";

  // components
  import FormatMoney from "@shared/svelte/Components/FormatMoney.svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import TableSortControl from "@shared/svelte/Components/TableSortControl.svelte";

  // services
  import { getDisplayAmount } from "@retailer/services/invoice_service";

  // types
  import type { Invoice } from "@shared/types/Invoice";

  // props
  export let invoices: Invoice[];
  export let viewInvoice: (invoiceId: number) => void;
  export let searchRan: boolean;
  export let showLoadingState: boolean;
  export let order: string;
  export let direction: string;
  export let handleSort: (sort: { order: string; direction: string }) => void;

  // data
  const loadingState = window["C"].IMAGE_PATHS["loadertransparent.gif"];
  let emptyState = {
    title: "No Paid Invoices",
    message: "There are no paid invoices in Provi.",
    search: "Nothing matches your search.",
  };

  // values
  let tableSorters: object;

  // reactives
  $: {
    tableSorters = {
      type: order || "",
      direction: direction || "",
      sortCallBack: sort => handleSort(sort),
    };
  }
</script>

<style>
  .sticky-header th {
    position: sticky;
    top: -1px;
    z-index: 10;
    background-color: #ffffff;
  }

  .loading-state {
    margin-left: -5px;
    height: 30px;
    width: 30px;
  }

  .alt-state-height {
    height: 100vh;
  }
</style>

<thead class="sticky-header">
  <tr>
    <th>Distributor</th>
    <th>
      <div class="fizz-flex-row-4">
        Due Date
        <TableSortControl sortFilter={tableSorters} colName={"due_date"} />
      </div>
    </th>
    <th>
      <div class="fizz-flex-row-4">
        Paid Date
        <TableSortControl sortFilter={tableSorters} colName={"payment_date_submitted"} />
      </div>
    </th>
    <th>
      <div class="fizz-flex-row-0">
        <span>Status</span>
        <span>
          <div class="fizz-tooltip-container fizz-flex-row-4">
            <button class="fizz-tooltip-trigger">
              <Icon type="InfoGray" height={16} width={16} />
            </button>
            <div
              class="fizz-tooltip fizz-tooltip-right"
              role="tooltip"
              data-testid="status-tooltip"
            >
              <p>
                <strong>Paid</strong><span> : Paid on Provi via ProviPay </span>
              </p>
              <p>
                <strong>Pending</strong><span>
                  : Payment or credit deposit has been submitted via ProviPay
                </span>
              </p>
              <p>
                <strong>Closed</strong><span>
                  : The invoice or credit is no longer outstanding, likely it was paid outside of
                  Provi
                </span>
              </p>
            </div>
          </div>
        </span>
      </div>
    </th>
    <th>Invoice</th>
    <th>Payment Confirmation Code</th>
    <th>Type</th>
    <th>Payment Method</th>
    <th class="fizz-text-right">Amount</th>
  </tr>
</thead>
<tbody>
  {#if showLoadingState && invoices.length == 0}
    <tr class="fizz-text-center alt-state-height">
      <td class="layout-1" colspan={8} style="padding-top: 8vh;">
        <div class="fizz-flex-row fizz-justify-center">
          <img alt="loader" src={loadingState} class="loading-state" />
        </div>
      </td>
    </tr>
  {:else if invoices.length > 0}
    {#each invoices as invoice}
      <tr
        id="invoice-{invoice.id}"
        on:click|preventDefault={() => viewInvoice(invoice.id)}
        class="cursor-pointer"
      >
        <td data-label="Distributor">{invoice.distributor.name}</td>
        <td data-label="Due Date">
          {LocaleString.convertISODateStringToTimeZoneSafeFormattedLocaleDate(invoice.due_date)}
        </td>
        <td data-label="Paid Date">
          {LocaleString.convertISODateStringToTimeZoneSafeFormattedLocaleDate(
            invoice.payment_date_submitted,
          )}
        </td>
        <td data-label="Status">
          <span
            class:fizz-pill-green={invoice.invoice_status_type == "paid"}
            class:fizz-pill-yellow={invoice.invoice_status_type == "pending"}
            class:fizz-pill-gray={invoice.invoice_status_type == "closed"}
            style="text-transform:capitalize;"
          >
            {invoice.invoice_status_type}
          </span>
        </td>
        <td data-label="Invoice">#{invoice.invoice_reference_id}</td>
        <td data-label="Payment Confirmation Code">
          {invoice.payment_confirmation ? invoice.payment_confirmation.confirmation_code : "--"}
        </td>
        <td data-label="Type">{parseFloat(invoice.amount_due) < 0 ? "Credit" : "Invoice"}</td>
        <td data-label="Payment Method"
          >{invoice.payment_method ? invoice.payment_method.nickname : "--"}</td
        >
        <td data-label="Amount" class="fizz-align-right">
          <FormatMoney input={getDisplayAmount(invoice)} formatType={"5"} />
        </td>
      </tr>
    {/each}
  {:else}
    <tr class="fizz-text-center alt-state-height">
      <td class="layout-1" colspan={8} style="padding-top: 8vh;">
        <strong class="padding--small">{emptyState.title}</strong>
        {#if searchRan}
          <div class="padding--small">{emptyState.search}</div>
        {:else}
          <div class="padding--smaller">{emptyState.message}</div>
        {/if}
      </td>
    </tr>
  {/if}
</tbody>
